import { Injectable } from '@angular/core'
import { BehaviorSubject, Subject } from 'rxjs'
import { environment } from 'src/environments/environment.prod'
import { Application } from 'src/app/account/services/application.service'
import { IdentityProviderDto } from 'src/app/services/dtos'
import { Table } from 'primeng/table'
import { LoggerService } from '../account/services/logger.service'

declare global {
  interface Window {
    HubSpotConversations: {
      clear: ({ resetWidget }) => void
      widget: {
        open: () => void
      }
    }
  }
}

export interface ErrorMessage {
  message: string
  detailedMessage: string
  // Optional severity discriminator — defaults to 'error' (existing behavior).
  // 'success' renders as a short-lived green confirmation pill via the same shell toast.
  severity?: 'error' | 'success'
  // When true, the toast won't auto-dismiss; the user must click its close button.
  // Used for actionable backend error details we don't want to flash and lose.
  sticky?: boolean
}

@Injectable({
  providedIn: 'root',
})
export class UiService {
  /**
   * This service provides general-purpose "shell" functionality needed by various UI
   * components, such as the ability to announce an error message that the shell can
   * display as appropriate.
   */

  constructor(private logger: LoggerService) {}

  get productName() {
    return environment.productName
  }

  get termsOfServiceUrl() {
    return environment.termsUrl
  }

  get documentationUrl() {
    return 'https://docs.arpio.io'
  }

  get contactUrl() {
    return 'https://arpio.io/contact-us/'
  }

  get cloudFormationInstructionsUrl() {
    return 'https://docs.aws.amazon.com/AWSCloudFormation/latest/UserGuide/cfn-console-create-stack.html'
  }

  get generateKeyPairInstructionsUrl() {
    return 'https://docs.aws.amazon.com/AWSEC2/latest/UserGuide/ec2-key-pairs.html#having-ec2-create-your-key-pair'
  }

  get importKeyPairInstructionsUrl() {
    return 'https://docs.aws.amazon.com/AWSEC2/latest/UserGuide/ec2-key-pairs.html#how-to-generate-your-own-key-and-import-it-to-aws'
  }

  get s3BucketReplicationDocs() {
    return 'https://docs.aws.amazon.com/AmazonS3/latest/userguide/replication.html'
  }

  get failbackDocsUrl() {
    return 'https://docs.arpio.io/failback'
  }

  get recoveryDocsUrl() {
    return 'https://docs.arpio.io/performing-a-recovery'
  }

  get resourceSelectionDocsUrl() {
    return 'https://docs.arpio.io/creating-an-application#resources'
  }

  get testVsRecoveryFaq() {
    return 'https://docs.arpio.io/arpio-test-vs-recovery-faq'
  }

  get rtReplicationDocsUrl() {
    return 'https://docs.arpio.io/real-time-rpo-replication'
  }

  get networkSandboxDocsUrl() {
    return 'https://docs.arpio.io/network-sandbox'
  }

  get azureNetworkSandboxDocsUrl() {
    return 'https://docs.arpio.io/network-sandbox'
  }

  get ransomwareDocsUrl() {
    return 'https://docs.arpio.io/ransomware-recovery'
  }

  get s3BucketSuffix() {
    return 'https://docs.arpio.io/s3#bucket-suffix'
  }

  get retentionDocsUrl() {
    return 'https://docs.arpio.io/retention-policy'
  }

  get rbacDocsUrl() {
    return 'https://docs.arpio.io/role-based-access-controls-rbac'
  }

  get s3BucketNamingRules() {
    return 'https://docs.aws.amazon.com/AmazonS3/latest/userguide/bucketnamingrules.html'
  }

  get supportContact() {
    return 'support@arpio.io'
  }

  get awsMarketplaceErrorUrl() {
    return '/aws_marketplace/error'
  }

  get azureMarketplaceErrorUrl() {
    return '/azure_marketplace/error'
  }

  get bucketReplicationInstructionsUrl() {
    return 'https://docs.aws.amazon.com/AmazonS3/latest/userguide/replication-example-walkthroughs.html'
  }

  get azureBlobReplicationUrl() {
    return 'https://learn.microsoft.com/en-us/azure/storage/blobs/object-replication-overview'
  }

  // setup a facility for components to publish errors that get presented
  // to the user in the broader application shell. Also carries success toasts
  // (see announceCopied) — distinguished by the `severity` field on ErrorMessage.
  private errorSource = new Subject<ErrorMessage>()
  public errorStream = this.errorSource.asObservable()

  // setup a facility for announcing new applications when they get created
  private applicationSource = new Subject<Application>()
  public applicationStream = this.applicationSource.asObservable()

  public bucketNameRegex: RegExp =
    /(?=^.{3,63}$)(?!^xn--)(?!.*-s3alias$)(?!^(\d{1,3}\.){3}\d{1,3}$)(^[a-z0-9]([a-z0-9-]*(\.[a-z0-9])?)*$)/

  // the public interface to publish a brief "Copied!" success toast — reuses
  // the error toast pipeline with severity='success' so there's one shell-managed
  // visual element instead of parallel toast systems.
  announceCopied(message: string = 'Copied to clipboard!') {
    this.errorSource.next({ message, detailedMessage: '', severity: 'success' })
  }

  // the public interface to announce errors.
  announceError(error: ErrorMessage) {
    // Convert a non-conforming error into an ErrorMessage object
    error.message = error.message || 'An unknown error occurred.'
    error.detailedMessage = error.detailedMessage || error.toString()
    this.errorSource.next(error)

    // Log when an error is annouced to Datadog
    this.logger.error(error.message)
  }

  /**
   * Handle and display API errors in a user-friendly way
   * Extracts meaningful error messages from the error object structure
   */
  announceApiError(error: any, fallbackMessage = 'An unexpected error occurred') {
    let message = fallbackMessage
    let detailedMessage = fallbackMessage
    let sticky = false

    // Check if this is an Arpio error with responseCode and body structure
    if (error && typeof error === 'object') {
      // Handle duplicate role name error (422 response code)
      if (error.responseCode === 422 && error.body?.message) {
        const bodyMessage = error.body.message.toLowerCase()
        if (bodyMessage.includes('role name already exists')) {
          message = 'Duplicate Role Name'
          detailedMessage = 'A role with this name already exists. Please choose a different name.'
          this.announceError({ message, detailedMessage })
          return
        }

        // Handle SAML metadata validation errors
        if (bodyMessage.includes('saml metadata')) {
          message = 'Invalid SAML Metadata'
          if (bodyMessage.includes('entitydescriptor')) {
            detailedMessage =
              'The SAML metadata you provided is invalid or expired. Please ensure you have copied the complete, current metadata XML from your identity provider. The metadata must contain a valid EntityDescriptor with an IDPSSODescriptor section.'
          } else if (bodyMessage.includes('expired')) {
            detailedMessage =
              'The SAML metadata you provided has expired. Please download fresh metadata XML from your identity provider and try again.'
          } else {
            detailedMessage =
              'The SAML metadata format is invalid. Please verify you have copied the complete XML metadata from your identity provider, including all opening and closing tags.'
          }
          this.announceError({ message, detailedMessage })
          return
        }
      }

      // Handle the specific email verification error
      if (error.responseCode === 403 && error.body?.message) {
        const bodyMessage = error.body.message
        if (bodyMessage.includes('verify your email address')) {
          message = 'Email Verification Required'
          detailedMessage =
            'You must verify your email address before you can add other users to this account. Please check your email for a verification link.'
        } else if (bodyMessage.includes('You must wait at least') && bodyMessage.includes('seconds')) {
          // Extract the wait time from the message
          const waitTimeMatch = bodyMessage.match(/(\d+(?:\.\d+)?)\s*seconds/)
          const waitTime = waitTimeMatch ? Math.ceil(parseFloat(waitTimeMatch[1])) : 120
          message = 'Please Wait'
          detailedMessage = `You must wait at least ${waitTime} seconds since the last email was sent before sending another. Please try again in a moment.`
        } else {
          message = 'Access Denied'
          detailedMessage = bodyMessage
        }
      }
      // Handle HTTP error codes — never expose raw API details to the user
      else if (error.responseCode !== undefined) {
        switch (error.responseCode) {
          case 0:
            message = 'Connection Error'
            detailedMessage = 'Unable to reach the server. Please check your connection and try again.'
            break
          case 400:
            // Backend-supplied 400/422 messages are user-facing by design (the API joins
            // per-app failures with `\n\n` so the toast can name which app/field failed).
            // Pin the toast (sticky=true) when we're showing the backend detail so the
            // user has time to read and copy it for troubleshooting; the generic fallback
            // stays auto-dismissing.
            message = 'Invalid Request'
            if (error.body?.message) {
              detailedMessage = error.body.message
              sticky = true
            } else {
              detailedMessage = 'The request was invalid. Please check your input and try again.'
            }
            break
          case 401:
            message = 'Authentication Required'
            detailedMessage = 'Please sign in to continue.'
            break
          case 403:
            message = 'Access Denied'
            detailedMessage = 'You do not have permission to perform this action.'
            break
          case 404:
            message = 'Not Found'
            detailedMessage = 'The requested resource could not be found.'
            break
          case 409:
            message = 'Conflict'
            detailedMessage = 'This action conflicts with the current state. Please refresh and try again.'
            break
          case 422:
            message = 'Invalid Input'
            if (error.body?.message) {
              detailedMessage = error.body.message
              sticky = true
            } else {
              detailedMessage = 'Please check your input and try again.'
            }
            break
          case 500:
            message = 'Server Error'
            detailedMessage =
              'An internal error occurred. Please try again later. Contact support if the error persists.'
            break
          default:
            message = 'Something Went Wrong'
            detailedMessage =
              'An unexpected error occurred. Please try again later. Contact support if the error persists.'
        }
      }
      // Handle other error formats
      else if (error.message) {
        message = 'Error'
        detailedMessage = error.message
      }
      // Handle detailedMessage if available
      else if (error.detailedMessage) {
        message = 'Error'
        detailedMessage = error.detailedMessage
      }
    }

    this.announceError({ message, detailedMessage, sticky })
  }

  // the public interface to announce applications
  announceApplication(application: Application) {
    this.applicationSource.next(application)
  }

  openChat() {
    if (window.HubSpotConversations) {
      window.HubSpotConversations.clear({ resetWidget: true })
      window.HubSpotConversations.widget.open()
    } else {
      this.announceError({
        message: 'Unable to find HubSpot chat widget.',
      } as ErrorMessage)
    }
  }

  buildRootUrl() {
    return '/'
  }

  buildNewApplicationUrl(accountId: string) {
    return `/${accountId}/applications/new`
  }

  buildNewApplicationAccessMethodUrl(accountId: string) {
    return `/${accountId}/applications/new/access-method`
  }

  buildNewApplicationResourcesUrl(accountId: string) {
    return `/${accountId}/applications/new/resources`
  }

  buildNewApplicationConnectionUrl(accountId: string) {
    return `/${accountId}/applications/new/connect`
  }

  buildApplicationUrl(accountId: string, applicationId: string) {
    return `/${accountId}/applications/${applicationId}`
  }

  buildCreateAccountUrl() {
    return '/create_account'
  }

  buildAccountUsersUrl(accountId: string) {
    return `/${accountId}/settings/user-management`
  }

  buildRetentionPolicyUrl(accountId: string) {
    return `/${accountId}/settings/account/retention`
  }

  buildResourceConfigsUrl(accountId: string) {
    return `/${accountId}/settings/account/resources`
  }

  buildIntegrationsUrl(accountId: string) {
    return `/${accountId}/settings/addons`
  }

  buildAccessManagementUrl(accountId: string) {
    return `/${accountId}/settings/access-management`
  }

  buildUserManagementUrl(accountId: string) {
    return `/${accountId}/settings/access-management`
  }

  buildRoleManagementUrl(accountId: string) {
    return `/${accountId}/settings/access-management/roles`
  }

  buildGroupManagementUrl(accountId: string) {
    return `/${accountId}/settings/access-management/groups`
  }

  buildApiKeyManagementUrl(accountId: string) {
    return `/${accountId}/settings/account/api-keys`
  }

  buildProfileUrl(accountId: string) {
    return `/${accountId}/profile`
  }

  buildAccountSupportUrl(accountId: string) {
    return `/${accountId}/support`
  }

  buildAccountResourceTagsUrl(accountId: string) {
    return `/${accountId}/settings/tags`
  }

  buildFinalizeInviteUrl() {
    // This URL should be absolute, because it gets passed into the invite
    // flow, which serves it back to the browser in a redirect response, possibly
    // from any host on the  internet.  In the native flow the redirect response
    // is handled by the UI.  For a SAML auth flow it's returned in response to
    // the POST to the ACS URL (and so it must be absolute to get us back here).
    return new URL('/auth/finishInvite', window.location.origin).toString()
  }

  buildAccountRootUrl(accountId: string) {
    return `/${accountId}`
  }

  buildAccountWelcomeUrl(accountId: string) {
    return `/${accountId}/welcome`
  }

  buildLoginUrl() {
    return '/auth/login'
  }

  buildResetPasswordUrl() {
    return '/auth/startResetPassword'
  }

  buildSyncPairUrl(
    accountId: string,
    sourceAwsAccountId: string,
    sourceRegion: string,
    targetAwsAccountId: string,
    targetRegion: string
  ) {
    return `/${accountId}/syncpairs/${sourceAwsAccountId}/${sourceRegion}/` + `${targetAwsAccountId}/${targetRegion}`
  }

  buildLinkAwsMarketplaceToAccountUrl(token: string = null) {
    if (!!token) {
      return `/aws_marketplace/link_account?awsToken=${token}`
    }
    return '/aws_marketplace/link_account'
  }

  buildLinkAzureMarketplaceToAccountUrl(token: string = null) {
    if (!!token) {
      return `/azure_marketplace/link_account?azureToken=${token}`
    }
    return '/azure_marketplace/link_account'
  }

  buildAccountSettingsUrl(accountId: string) {
    return `/${accountId}/settings/account`
  }

  buildAbsoluteAccountSettingsUrl(accountId: string) {
    return new URL(`/${accountId}/settings/account`, window.location.origin).toString()
  }

  buildSubscriptionSettingsUrl(accountId: string) {
    // for now, subscriptions are viewed on the account settings page
    return this.buildAccountSettingsUrl(accountId)
  }

  buildIdentityProviderName(idp: IdentityProviderDto): string {
    if (idp.type.toLowerCase() === 'native') {
      return idp.name
    } else {
      return `${idp.name} (${idp.type.toUpperCase()})`
    }
  }

  get drsAgentInstallationInstructionsUrl() {
    return 'https://docs.aws.amazon.com/drs/latest/userguide/agent-installation.html'
  }

  isCustomComponent$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)

  /**
   * Sorts a PrimeNG Table based on specified field and order.
   * This function ensures that the sorting operation is deferred until the table is fully initialized,
   * which is particularly useful when called from lifecycle hooks like ngAfterViewInit.
   *
   * @param {Table} table - The PrimeNG Table instance to sort.
   * @param {string} sortField - The field name on which to sort the table.
   * @param {number} sortOrder - The order of sorting: 1 for ascending, -1 for descending.
   */
  sortTable(table: Table, sortField: string, sortOrder: number): void {
    if (table) {
      setTimeout(() => {
        table.sortField = sortField
        table.sortOrder = sortOrder // 1 for ascending, -1 for descending
        table.sortSingle() // Perform the sort operation
      })
    }
  }
}
